import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router';

const LAST_UPDATED_DATE = 'December 14, 2025';

export const Eula = () => {
  return (
    <div className="text-left bg-white py-12 sm:py-16 flex-grow">
      <div className="mx-auto max-w-4xl px-6 lg:px-8">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm font-medium text-primary-50 hover:text-primary-t-100 mb-6"
        >
          <ArrowLeftIcon className="size-4" />
          Back to Home
        </Link>
        <h1 className="text-2xl font-semibold text-grey-100 sm:text-3xl">
          End User License Agreement (EULA)
        </h1>
        <div className="mt-8 space-y-6 text-sm/6 text-grey-400 text-left">
          <p>Last updated: {LAST_UPDATED_DATE}</p>

          <p className="text-grey-400 italic">
            This document is incorporated by reference into the Marketplace AI Master Terms of
            Service. Acceptance of the Master Terms constitutes acceptance of this EULA and Privacy
            Policy.
          </p>

          <p className="text-grey-400">
            This EULA governs all Marketplace AI software, APIs, and interfaces regardless of URL.
          </p>

          {/* Section 1 */}
          <div>
            <h3 className="text-xl font-semibold text-grey-100">1. Scope and Acceptance</h3>
            <div className="mt-2 text-grey-400 space-y-2">
              <p>
                This End‑User License Agreement (<strong>"EULA"</strong>) governs your access to and
                use of all software, applications, interfaces, APIs, edge devices, and related
                services provided by Marketplace AI, Inc. (<strong>"Company,"</strong>{' '}
                <strong>"we,"</strong> <strong>"us,"</strong> or <strong>"our"</strong>), regardless
                of product name or URL.
              </p>
              <p>Covered products include, without limitation:</p>
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>HealthcareLX</li>
                <li>MedCredVault</li>
                <li>Any successor, beta, pilot, or integrated Marketplace AI offerings</li>
              </ul>
              <p>
                By creating an account, clicking "I Agree," or otherwise accessing the Platform, you
                accept this EULA as a binding agreement.
              </p>
            </div>
          </div>

          {/* Section 2 */}
          <div>
            <h3 className="text-xl font-semibold text-grey-100">2. License Grant</h3>
            <div className="mt-2 text-grey-400 space-y-2">
              <p>
                Subject to your compliance with the Master Terms and all incorporated agreements,
                Company grants you a{' '}
                <strong>limited, non‑exclusive, non‑transferable, revocable license</strong> to
                access and use the Platform solely for your internal business or professional
                purposes as intended.
              </p>
              <p>No ownership rights are transferred under this EULA.</p>
            </div>
          </div>

          {/* Section 3 */}
          <div>
            <h3 className="text-xl font-semibold text-grey-100">3. License Restrictions</h3>
            <div className="mt-2 text-grey-400">
              <p>You may not:</p>
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>
                  Copy, modify, reverse engineer, decompile, or create derivative works of the
                  Platform
                </li>
                <li>Circumvent security controls, access controls, or audit mechanisms</li>
                <li>
                  Use the Platform to violate law, professional regulations, or third‑party rights
                </li>
                <li>
                  Access the Platform to compete with Company or build a substantially similar
                  product
                </li>
                <li>Use automated scraping, bots, or data harvesting except via approved APIs</li>
              </ul>
            </div>
          </div>

          {/* Section 4 */}
          <div>
            <h3 className="text-xl font-semibold text-grey-100">4. Availability and Changes</h3>
            <p className="mt-2 text-grey-400">
              The Platform may evolve over time. We may modify, suspend, or discontinue features
              without liability. We do not guarantee uninterrupted availability.
            </p>
          </div>

          {/* Section 5 */}
          <div>
            <h3 className="text-xl font-semibold text-grey-100">5. Intellectual Property</h3>
            <div className="mt-2 text-grey-400 space-y-2">
              <p>
                All intellectual property rights in the Platform — including software, workflows,
                analytics models, interfaces, trademarks, and documentation — are owned by Company
                or its licensors.
              </p>
              <p>
                <strong>Feedback.</strong> Any feedback or suggestions you provide may be used by
                Company without restriction or compensation.
              </p>
            </div>
          </div>

          {/* Section 6 */}
          <div>
            <h3 className="text-xl font-semibold text-grey-100">
              6. No Employment or Agency Relationship
            </h3>
            <div className="mt-2 text-grey-400">
              <p>
                Except where expressly stated in a written Order Form, Company is{' '}
                <strong>not</strong>:
              </p>
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>An employer</li>
                <li>A staffing agency</li>
                <li>A placement firm</li>
                <li>A medical provider</li>
              </ul>
              <p className="mt-2">Company provides infrastructure only.</p>
            </div>
          </div>

          {/* Section 7 */}
          <div>
            <h3 className="text-xl font-semibold text-grey-100">7. Termination</h3>
            <p className="mt-2 text-grey-400">
              Your license terminates automatically upon breach of this EULA or termination of your
              account. Sections relating to IP, disclaimers, liability limits, and dispute
              resolution survive termination.
            </p>
          </div>

          {/* Section 8 */}
          <div>
            <h3 className="text-xl font-semibold text-grey-100">8. Disclaimer of Warranties</h3>
            <p className="mt-2 text-grey-400 uppercase font-semibold">
              THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE." COMPANY DISCLAIMS ALL WARRANTIES,
              EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
              NON‑INFRINGEMENT, TO THE MAXIMUM EXTENT PERMITTED BY LAW.
            </p>
          </div>

          {/* Section 9 */}
          <div>
            <h3 className="text-xl font-semibold text-grey-100">9. Limitation of Liability</h3>
            <div className="mt-2 text-grey-400 space-y-2">
              <p>
                To the fullest extent permitted by law, Company shall not be liable for indirect,
                incidental, special, consequential, or punitive damages. Company's aggregate
                liability shall not exceed the greater of:
              </p>
              <ul className="list-disc pl-5 mt-1 space-y-1">
                <li>Fees paid by you to Company in the prior 6 months, or</li>
                <li>$1,000 USD</li>
              </ul>
            </div>
          </div>

          {/* Section 10 */}
          <div>
            <h3 className="text-xl font-semibold text-grey-100">10. Governing Law and Forum</h3>
            <p className="mt-2 text-grey-400">
              This EULA is governed by the same governing law and dispute‑resolution provisions set
              forth in the Master Terms of Service.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
